import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Package, Heart, MapPin, User, Lock, Ticket, LogOut, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, brl, d, errMsg, ORDER_STATUS, useSeo } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import ProductCard from "@/components/store/ProductCard";
import { Timeline, OrderItems } from "@/components/store/OrderParts";

function AuthForms() {
  const { setCustomer } = useStore();
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const [mode, setMode] = useState("login");
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const up = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "forgot") { await api.post("/auth/forgot-password", { email: f.email, origin: window.location.origin }); toast.success("Se o e-mail existir, enviaremos as instruções."); setMode("login"); }
      else { const r = await api.post(mode === "login" ? "/auth/login" : "/auth/register", f); await setCustomer(r.data); toast.success("Bem-vindo(a)!"); if (sp.get("next")) nav(sp.get("next")); }
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <div className="max-w-md mx-auto px-4 py-16">
      <h1 className="text-3xl font-bold">{mode === "login" ? "Entrar" : mode === "register" ? "Criar conta" : "Recuperar senha"}</h1>
      <p className="text-sm text-[#A0A0A0] mt-2">Acompanhe pedidos, salve favoritos e endereços.</p>
      <form onSubmit={submit} className="card p-6 mt-8 space-y-4" data-testid="customer-auth-form">
        {mode === "register" && <div className="grid grid-cols-2 gap-3"><div><label className="label">Nome</label><input className="input" required onChange={up("first_name")} data-testid="register-first-name" /></div><div><label className="label">Sobrenome</label><input className="input" required onChange={up("last_name")} data-testid="register-last-name" /></div></div>}
        <div><label className="label">E-mail</label><input className="input" type="email" required autoComplete="email" onChange={up("email")} data-testid="auth-email" /></div>
        {mode !== "forgot" && <div><label className="label">Senha</label><input className="input" type="password" required minLength={mode === "register" ? 8 : 1} autoComplete={mode === "login" ? "current-password" : "new-password"} onChange={up("password")} data-testid="auth-password" /></div>}
        <button className="btn-primary w-full py-3.5" disabled={busy} data-testid="auth-submit">{mode === "login" ? "Entrar" : mode === "register" ? "Criar conta" : "Enviar link"}</button>
        <div className="flex justify-between text-sm">
          <button type="button" className="text-[#00C8FF]" onClick={() => setMode(mode === "login" ? "register" : "login")} data-testid="auth-toggle">{mode === "login" ? "Criar conta" : "Já tenho conta"}</button>
          {mode === "login" && <button type="button" className="text-[#A0A0A0]" onClick={() => setMode("forgot")} data-testid="auth-forgot">Esqueci minha senha</button>}
        </div>
        <p className="text-xs text-[#6b7079] text-center">Login com Google: recurso futuro.</p>
      </form>
    </div>
  );
}

function Orders() {
  const [list, setList] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => { api.get("/account/orders").then((r) => setList(r.data)); }, []);
  if (!list) return <div className="h-40 bg-[#171A1F] rounded-2xl animate-pulse" />;
  if (!list.length) return <p className="text-[#A0A0A0]">Você ainda não fez pedidos. <Link className="text-[#00C8FF]" to="/produtos">Ver produtos</Link></p>;
  return (
    <div className="space-y-3" data-testid="account-orders">
      {list.map((o) => (
        <div key={o.id} className="card p-5">
          <button className="w-full flex flex-wrap justify-between gap-2 text-left" onClick={() => setOpen(open === o.id ? null : o.id)} data-testid={`account-order-${o.number}`}>
            <div><div className="font-mono font-semibold">{o.number}</div><div className="text-xs text-[#A0A0A0]">{d(o.created_at)}</div></div>
            <span className="chip bg-[#008CFF]/10 text-[#00C8FF] h-fit">{ORDER_STATUS[o.status]}</span><b>{brl(o.total)}</b>
          </button>
          {open === o.id && <div className="grid md:grid-cols-2 gap-6 mt-6 pt-6 border-t border-[#282D37]"><OrderItems order={o} /><Timeline order={o} /></div>}
        </div>
      ))}
    </div>
  );
}

function Favorites() {
  const { favorites } = useStore();
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/account/favorites/products", { params: { ids: favorites.join(",") } }).then((r) => setItems(r.data)); }, [favorites]);
  if (!items.length) return <p className="text-[#A0A0A0]" data-testid="favorites-empty">Nenhum favorito ainda.</p>;
  return <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="favorites-grid">{items.map((p) => <ProductCard key={p.id} p={p} />)}</div>;
}

export function FavoritesPage() {
  useSeo({ title: "Favoritos" });
  return <div className="max-w-[1280px] mx-auto px-4 lg:px-8 py-10"><h1 className="text-3xl font-bold mb-8">Favoritos</h1><Favorites /></div>;
}

function Addresses() {
  const { customer } = useStore();
  const [list, setList] = useState(customer.addresses || []);
  const save = async (l) => { setList(l); await api.put("/account/addresses", { addresses: l }); toast.success("Endereços atualizados"); };
  return (
    <div className="space-y-3" data-testid="account-addresses">
      {!list.length && <p className="text-[#A0A0A0]">Endereços usados no checkout aparecem aqui.</p>}
      {list.map((a, k) => (
        <div key={k} className="card p-4 flex justify-between gap-3 text-sm"><span>{a.street}, {a.number} {a.complement} — {a.district}, {a.city}/{a.state} · {a.cep}</span>
          <button onClick={() => save(list.filter((_, i) => i !== k))} aria-label="Remover endereço" className="text-[#A0A0A0] hover:text-red-400"><Trash2 className="h-4 w-4" /></button></div>
      ))}
    </div>
  );
}

function Profile() {
  const { customer, setCustomer } = useStore();
  const [f, setF] = useState(customer);
  const save = async () => { try { const r = await api.put("/account/profile", f); setCustomer(r.data); toast.success("Dados salvos"); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="card p-6 grid sm:grid-cols-2 gap-4 max-w-2xl" data-testid="account-profile">
      {[["first_name", "Nome"], ["last_name", "Sobrenome"], ["cpf", "CPF"], ["phone", "Telefone"]].map(([k, l]) => <div key={k}><label className="label">{l}</label><input className="input" value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`profile-${k}`} /></div>)}
      <div className="sm:col-span-2"><label className="label">E-mail</label><input className="input opacity-60" value={f.email} disabled /></div>
      <button className="btn-primary sm:col-span-2" onClick={save} data-testid="profile-save">Salvar</button>
    </div>
  );
}

function Password() {
  const [f, setF] = useState({});
  const save = async () => { try { await api.put("/account/password", f); toast.success("Senha alterada"); setF({}); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="card p-6 space-y-4 max-w-md" data-testid="account-password">
      <div><label className="label">Senha atual</label><input type="password" className="input" value={f.current || ""} onChange={(e) => setF({ ...f, current: e.target.value })} data-testid="pwd-current" /></div>
      <div><label className="label">Nova senha</label><input type="password" className="input" value={f.new || ""} onChange={(e) => setF({ ...f, new: e.target.value })} data-testid="pwd-new" /></div>
      <button className="btn-primary w-full" onClick={save} data-testid="pwd-save">Alterar senha</button>
    </div>
  );
}

function Coupons() {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/account/coupons").then((r) => setList(r.data)); }, []);
  if (!list.length) return <p className="text-[#A0A0A0]">Nenhum cupom disponível.</p>;
  return <div className="grid sm:grid-cols-2 gap-3">{list.map((c) => <div key={c.id} className="card p-5 border-dashed"><div className="font-mono text-lg font-bold text-[#00C8FF]">{c.code}</div><div className="text-sm text-[#A0A0A0]">{c.type === "percent" ? `${c.value}% off` : c.type === "fixed" ? `${brl(c.value)} off` : "Frete grátis"}{c.min_value ? ` · mínimo ${brl(c.min_value)}` : ""}</div></div>)}</div>;
}

const TABS = [["pedidos", "Meus pedidos", Package, Orders], ["favoritos", "Favoritos", Heart, Favorites], ["enderecos", "Endereços", MapPin, Addresses], ["dados", "Dados pessoais", User, Profile], ["senha", "Alterar senha", Lock, Password], ["cupons", "Cupons", Ticket, Coupons]];

export default function Account() {
  const { customer, logout } = useStore();
  const [sp, setSp] = useSearchParams();
  useSeo({ title: "Minha conta" });
  if (customer === undefined) return <div className="py-32" />;
  if (!customer) return <AuthForms />;
  const tab = sp.get("tab") || "pedidos";
  const Comp = TABS.find((t) => t[0] === tab)?.[3] || Orders;
  return (
    <div className="max-w-[1280px] mx-auto px-4 lg:px-8 py-10">
      <h1 className="text-3xl font-bold">Olá, {customer.first_name}</h1>
      <div className="grid lg:grid-cols-[240px_1fr] gap-8 mt-8">
        <nav className="flex lg:flex-col gap-1 overflow-x-auto scrollbar-none" data-testid="account-nav">
          {TABS.map(([k, l, I]) => <button key={k} onClick={() => setSp({ tab: k })} className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold whitespace-nowrap ${tab === k ? "bg-[#171A1F] text-white" : "text-[#A0A0A0] hover:text-white"}`} data-testid={`account-tab-${k}`}><I className="h-4 w-4" />{l}</button>)}
          <button onClick={logout} className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-[#A0A0A0] hover:text-red-400" data-testid="account-logout"><LogOut className="h-4 w-4" />Sair</button>
        </nav>
        <div><Comp /></div>
      </div>
    </div>
  );
}

export function ResetPassword({ admin }) {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const [pwd, setPwd] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post(admin ? "/admin/auth/reset-password" : "/auth/reset-password", { token: sp.get("token"), password: pwd }); toast.success("Senha redefinida"); nav(admin ? "/administrativo/login" : "/minha-conta"); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div className="max-w-md mx-auto px-4 py-20 min-h-screen">
      <h1 className="text-3xl font-bold">Nova senha</h1>
      <form onSubmit={submit} className="card p-6 mt-8 space-y-4"><div><label className="label">Nova senha (8+ caracteres)</label><input type="password" minLength={8} required className="input" value={pwd} onChange={(e) => setPwd(e.target.value)} data-testid="reset-password-input" /></div>
        <button className="btn-primary w-full" data-testid="reset-password-submit">Redefinir senha</button></form>
    </div>
  );
}
